import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { useMe } from '@/lib/me.js';
import { artigosPara, buscarArtigos, type Artigo } from '@/features/ajuda/artigos.js';

/**
 * Central de ajuda do app (/ajuda). Cada pessoa vê os artigos do que tem
 * acesso. O conteúdo está em features/ajuda/artigos.tsx.
 */

const ORDEM: Artigo['categoria'][] = ['Começando', 'Método', 'Conhecimento', 'Conselheiro', 'Conta'];

export function AjudaPage() {
  const me = useMe();
  const [termo, setTermo] = useState('');
  const visiveis = useMemo(() => artigosPara(me.data), [me.data]);
  const achados = useMemo(() => buscarArtigos(visiveis, termo), [visiveis, termo]);

  return (
    <PageLayout title="Ajuda" description="Como usar a Hack Academy. Procure pelo assunto ou navegue pelos temas.">
      <input
        type="search"
        value={termo}
        onChange={(e) => setTermo(e.target.value)}
        placeholder="Procure: senha, radar, indicador, certificado…"
        aria-label="Procurar na ajuda"
        className="mb-6 w-full rounded-element border border-ha-border2 bg-transparent px-3 py-2.5 text-sm"
      />
      {achados.length === 0 ? (
        <p className="text-sm text-ha-subtle">Nada encontrado para "{termo}". Tente outra palavra.</p>
      ) : (
        <div className="flex flex-col gap-6">
          {ORDEM.map((cat) => {
            const daCategoria = achados.filter((a) => a.categoria === cat);
            if (daCategoria.length === 0) return null;
            return (
              <section key={cat}>
                <h2 className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">{cat}</h2>
                <ul className="grid gap-3 sm:grid-cols-2">
                  {daCategoria.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/ajuda/${a.slug}`}
                        className="block h-full rounded-card border border-ha-border2 bg-ha-card p-4 hover:border-accent"
                      >
                        <span className="font-semibold text-ha-text">{a.titulo}</span>
                        <span className="mt-1 block text-sm text-ha-subtle">{a.resumo}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </PageLayout>
  );
}

export function AjudaArtigoPage() {
  const { slug = '' } = useParams();
  const me = useMe();
  const visiveis = useMemo(() => artigosPara(me.data), [me.data]);
  const artigo = visiveis.find((a) => a.slug === slug);

  if (me.isLoading) return <PageLayout title="Ajuda">Carregando…</PageLayout>;
  if (!artigo) {
    return (
      <PageLayout title="Ajuda">
        <p className="text-sm text-ha-subtle">Artigo não encontrado.</p>
        <Link to="/ajuda" className="mt-3 inline-block text-sm text-accent underline">
          Ver todos os artigos
        </Link>
      </PageLayout>
    );
  }
  const relacionados = visiveis.filter((a) => a.categoria === artigo.categoria && a.slug !== artigo.slug);
  return (
    <PageLayout
      title={artigo.titulo}
      description={artigo.resumo}
      actions={
        <Link to="/ajuda" className="text-xs text-accent underline">
          ← Ajuda
        </Link>
      }
    >
      <article className="ajuda-artigo max-w-3xl rounded-card border border-ha-border2 bg-ha-card p-6 text-sm leading-relaxed text-ha-text">
        {artigo.corpo}
      </article>
      {relacionados.length > 0 ? (
        <div className="mt-6">
          <h2 className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-wider text-ha-muted">Veja também</h2>
          <ul className="flex flex-wrap gap-3 text-sm">
            {relacionados.map((a) => (
              <li key={a.slug}>
                <Link to={`/ajuda/${a.slug}`} className="text-accent underline">
                  {a.titulo}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </PageLayout>
  );
}
